import { useEffect, useState, createContext, useContext } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { MdCreate } from "react-icons/md";

import { getCategoryWithProjects } from '../../api/category.api';
import { projectOrder } from '../../api/project.api';
import ExpandableProjectsComponent from '../../components/Project/ExpandableProjectComponent';
import ToolbarOptionComponent from '../../components/toolbar/ToolbarOptionComponent';
import {ProjectModal} from '../../components/Project/ProjectModal';
import { toolbarActions } from '../../services/EnumsService';
import { getContrastColor } from '../../services/stringServices';
import { showError } from '../../services/toastServices';
import ToolbarComponent from '../../components/toolbar/ToolbarComponent';

const CategoryContext = createContext();
export const CategoryData = () => useContext(CategoryContext);

export const CategoryWrapper = ()=>{
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [category, setCategory] = useState({projects:[]})
    const [showProjectModal, setShowProjectModal] = useState(false)
    const [expandedProjectId, setExpandedProjectId] = useState(null)
    const [expandedTaskId, setExpandedTaskId] = useState(null)
    const [dragProjectId, setDragProjectId] = useState(null)
    const [dragOverProjectId, setDragOverProjectId] = useState(null)
    const [isProjectDragging, setIsProjectDragging] = useState(false)

    async function loadCategoryProjects() {
        if (params.id) {
            const res = await getCategoryWithProjects(params.id)
            setCategory(res.data)
            
            // Expandir proyecto si viene en query params o state
            const projectIdFromQuery = searchParams.get('projectId');
            const stateData = location.state;
            
            if (stateData?.expandProjectId) {
                setExpandedProjectId(stateData.expandProjectId);
                setExpandedTaskId(stateData.expandTaskId || null);
                // Actualizar URL con query params
                navigate(`/category-projects/${params.id}?projectId=${stateData.expandProjectId}`, { 
                    replace: true,
                    state: null 
                });
            } else if (projectIdFromQuery) {
                setExpandedProjectId(parseInt(projectIdFromQuery));
            } else {
                // Si no hay projectId en la URL, limpiar el estado
                setExpandedProjectId(null);
                setExpandedTaskId(null);
            }
        }
    }

    const addProjectAction = (()=>{
        setShowProjectModal(true)
    })
    const addProjectCloseAction = (()=>{
        setShowProjectModal(false)
    })

    useEffect(() => {
        loadCategoryProjects()
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id, searchParams]);

    const refreshProjectsCategory = async (category) => {
        await loadCategoryProjects()
    }

    const renderProjects = () => {
        if(category === undefined || category.projects === undefined) return
        const orderedProjects = [...category.projects].sort((a, b) => a.order - b.order)

        const handleProjectDrop = async (toIndex, draggedProjectId) => {
            const newOrder = toIndex
            try {
                await projectOrder(draggedProjectId, newOrder, (response) => {
                    loadCategoryProjects()
                }, (error) => {
                    showError(`Error al reordenar el proyecto. ${error.message}`)
                })
                loadCategoryProjects()
            } catch (error) {
                showError('Error al reordenar el proyecto')
            }
        }

        return orderedProjects.map((project, index) => (
            <div
                key={project.id}
                draggable
                onDragStart={() => {
                    setDragProjectId(project.id)
                    setIsProjectDragging(true)
                }}
                onDragOver={(e) => {
                    e.preventDefault()
                    if (dragProjectId && dragProjectId !== project.id) {
                        setDragOverProjectId(project.id)
                    }
                }}
                onDragLeave={() => setDragOverProjectId(null)}
                onDragEnd={() => {
                    setDragProjectId(null)
                    setDragOverProjectId(null)
                    setIsProjectDragging(false)
                }}
                onDrop={async (e) => {
                    e.preventDefault()
                    if (!dragProjectId || dragProjectId === project.id) return
                    await handleProjectDrop(index, dragProjectId)
                    setDragProjectId(null)
                    setDragOverProjectId(null)
                    setIsProjectDragging(false)
                }}
                className={`mb-3 ${
                    dragOverProjectId === project.id ? 'ring-1 ring-blue-300 rounded-md' : ''
                }`}
            >
                <ExpandableProjectsComponent
                    project={project}
                    expanded={expandedProjectId === project.id}
                    expandedTaskId={expandedProjectId === project.id ? expandedTaskId : null}
                    onToggle={(nextExpanded) => {
                        if (nextExpanded) {
                            setExpandedProjectId(project.id);
                            navigate(`/category-projects/${params.id}?projectId=${project.id}`, { replace: true });
                        } else {
                            setExpandedProjectId(null);
                            setExpandedTaskId(null);
                            navigate(`/category-projects/${params.id}`, { replace: true });
                        }
                    }}
                />
            </div>
        ))
    }

    const addProjectModal = (<ProjectModal category={category} closeAction={addProjectCloseAction}></ProjectModal>)
    const handleEdit = (event) => {
        if(event.metaKey || event.ctrlKey) return;
        event.preventDefault();
        navigate(`/category/${category.id}`)
    }
    
    return(
        <div className='mb-2 rounded-lg'>
            <CategoryContext.Provider value={{category, refreshProjectsCategory}}>
                {showProjectModal && addProjectModal}
                <div className='mt-2 ml-2'>
                    <div className='grid grid-cols-3'>
                        <div className='col-span-3 flex flex-row items-center'>
                            <div className="w-[30px] h-[30px] rounded-full mb-3 mr-2 font-bold text-center text-lg"
                                style={{ 
                                    backgroundColor: category.color || '#6B7280',
                                    color: category.color ? getContrastColor(category.color) : '#ffffff'
                                }}>
                                {category.projects.length}
                            </div>
                            
                            <div className='font-bold text-2xl mb-3'>{category.name}</div>
                            <ToolbarComponent className="ml-2 mb-3">
                                <ToolbarOptionComponent title={'+ Proyecto'} size={25} icon={toolbarActions.AddProject} clickAction={addProjectAction}/>
                                <ToolbarOptionComponent title={'Editar'} size={25} icon={toolbarActions.Edit} clickAction={handleEdit}/>                            
                            </ToolbarComponent>
                        </div>
                    </div>
                    <div className='w-full pr-2'>
                        {renderProjects()}
                    </div>
                </div>
            </CategoryContext.Provider>
        </div>
    )
}